import { memo, useCallback, useEffect, useRef, useState } from 'react';
import CartNotificationGroup from './CartNotificationGroup';
import { ToastContainer } from 'react-toastify';
import { useOutletContext } from 'react-router-dom';
import setTitle from '../../../components/common/SetTitle';
import { OutletIProps } from '../../../components/sitebase/Base.types';
import LoadingSpinner from '../../../components/loadingSpinner/LoadingSpinner';
import type { CartNotificationData, EFASNotification } from '../notifications/Notification.types';


interface ActiveNotificationData extends EFASNotification {
    data_summary_parsed: CartNotificationData
}

// Pure filter helpers live at module scope so the fetch callback can depend on
// them without them being recreated on every render.
const filterNotificationsByPartner = (relevantNotifications: ActiveNotificationData[], partner: string) => {
    if (partner === "All") {
        return relevantNotifications;
    } else {
        return relevantNotifications.filter(n => n.data_summary_parsed.partner?.abbreviation === partner);
    }
}

const filterNotificationsByType = (relevantNotifications: ActiveNotificationData[], type: string) => {
    if (type === "Formal") {
        return relevantNotifications.filter(n => n.notification_type === 1);
    } else if (type === "Informal") {
        return relevantNotifications.filter(n => n.notification_type === 2);
    } else if (type === "Flash Flood") {
        return relevantNotifications.filter(n => n.notification_type === 3);
    } else { // All
        return relevantNotifications;
    }
}

const filterNotifications = (allNotifications: ActiveNotificationData[], partner: string, type: string) => {
    let filteredNotifications = filterNotificationsByPartner(allNotifications, partner);
    filteredNotifications = filterNotificationsByType(filteredNotifications, type);
    return filteredNotifications;
}

const links = [
    { name: 'Cart', url: `/cart` },
    { name: 'Active Notifications', url: `/cart/active-notifications`, active: true },
    { name: 'Deactivated Notifications', url: `/cart/deactivated-notifications` },
];

export default memo(function ActiveNotificationsApp() {
    const { setExtraMenuLinks } = useOutletContext<OutletIProps>();
    // tack on the data_summary_parsed field to each notification's object
    const [notifications, setNotifications] = useState<ActiveNotificationData[]>([]);
    const [visibleNotifications, setVisibleNotifications] = useState<ActiveNotificationData[]>([]);
    const [partners, setPartners] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [partnerFilter, setPartnerFilter] = useState<string>("All");
    const [typeFilter, setTypeFilter] = useState<string>("All");

    // Use refs so the fetch callback always sees the latest filter values
    const partnerFilterRef = useRef(partnerFilter);
    partnerFilterRef.current = partnerFilter;
    const typeFilterRef = useRef(typeFilter);
    typeFilterRef.current = typeFilter;

    const fetchNotificationsData = useCallback(() => {
        setLoading(true);
        fetch(`/api/notifications/sent/?format=json&max=0&active=true&time=${Math.random()}`)
            .then((response) => {
                return response.json();
            }).then((data: EFASNotification[]) => {
                const data_summary_parsed = data.map(n => {
                    return {
                        ...n,
                        data_summary_parsed: JSON.parse(n.data_summary)
                    };
                });
                setNotifications(data_summary_parsed);
                setVisibleNotifications(
                    filterNotifications(data_summary_parsed, partnerFilterRef.current, typeFilterRef.current)
                );
            }).catch((error) => {
                console.error('Error fetching notifications data:', error);
            }).finally(() => {
                setLoading(false);
            });
    }, []);


    useEffect(() => {
        setTitle('Cart');
        window.scrollTo({ top: 0, behavior: 'instant' });
        setExtraMenuLinks(links);

        fetchNotificationsData();
    }, [fetchNotificationsData, setExtraMenuLinks]);

    useEffect(() => {
        if (notifications.length === 0) return;
        const uniquePartners = new Set<string>();
        notifications.forEach(n => uniquePartners.add(n.data_summary_parsed.partner?.abbreviation || "Unknown"));
        setPartners(Array.from(uniquePartners).sort());
    }, [notifications]);

    return (<>
        <ToastContainer position='top-center' />
        <div className='d-flex flex-row mt-2'>
            <div className="input-group">
                <label className="input-group-text">Dissemination Centre</label>
                <select onChange={(e) => {
                    const partner = e.target.value;
                    setPartnerFilter(partner);
                    setVisibleNotifications(filterNotifications(notifications, partner, typeFilter));
                }}
                    className="form-select"
                    style={{ width: "10rem" }}>

                    <option>All</option>
                    {partners.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
            </div>
            <div className="input-group">
                <label className="input-group-text">Notification Type</label>
                <select onChange={(e) => {
                    const type = e.target.value;
                    setTypeFilter(type);
                    setVisibleNotifications(filterNotifications(notifications, partnerFilter, type));
                }}
                    style={{ width: "10rem" }}
                    className="form-select"
                >
                    <option>All</option>
                    <option>Formal</option>
                    <option>Informal</option>
                    <option>Flash Flood</option>
                </select>
            </div>
        </div>

        <div key={visibleNotifications.length} className='d-flex flex-column mt-2' id='active-notifications-list'>
            {loading ? (
                <div className='text-center py-5'><LoadingSpinner /></div>
            ) : visibleNotifications.length === 0 ? (
                <div className='py-5 text-center'>
                    {notifications.length === 0
                        ? 'No active notifications.'
                        : 'No notifications match the selected filters.'}
                </div>
            ) : (
                visibleNotifications.map((notification, index) =>
                    <CartNotificationGroup key={index} notification={notification} fetchNotificationsData={fetchNotificationsData} showClosedTimestamp={false} allowResend />
                )
            )}
        </div>
    </>);
})
